import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import type {
  EntityDetails,
  EntityDetailsInventory,
  LinesCoverageFile,
  InventoryCoverageFile,
} from '@/components/smartai/api/getCoverageAggergate';
import { api } from '@/shared/axios-client';
import type { RootState } from '@/state/store';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import CoverageDashboardSkeleton, {
  CoverageDashboardContentSkeleton,
} from './CoverageDashboardSkeleton';
import { useCoverageAggregateManual, COVERAGE_FILES_PAGE_SIZE } from '../api/getCoverageAggergateManual';
import {
  coverageCardClass,
  coveragePageClass,
  CoverageModeExplanation,
  CoverageModeToolbar,
  CoverageStateMessage,
  PollerBadges,
  type CoverageMode,
} from './coverage-dashboard-layout';
import { CoverageHistoryNavLink } from './CoverageHistoryNavLink';
import {
  ElementBreakdown,
  EntityDetailsPanel,
  FileCoverageFilterBar,
  filterInventoryFiles,
  filterLinesFiles,
  InventoryTable,
  LinesTable,
  SummaryCards,
  useAutoLoadCoveragePages,
  ViewCodeModal,
  type FileCoverageFilter,
} from './coverage-dashboard-shared';

function mergeCoverageFilesByPath<T extends { filePath: string }>(
  prev: T[],
  next: T[],
): T[] {
  if (!next.length) return prev;
  const seen = new Set(prev.map(f => f.filePath));
  const appended = next.filter(f => f.filePath && !seen.has(f.filePath));
  return appended.length ? [...prev, ...appended] : prev;
}

const ComponentCoverageViewManual = () => {
  const { ready, isLoading, notFound } = useSelectedComponent();
  const selected = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );

  const [mode, setMode] = useState<CoverageMode>('lines');
  const [viewCodeFilePath, setViewCodeFilePath] = useState<string | null>(null);
  const [detailFilePath, setDetailFilePath] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  const [currentOffset, setCurrentOffset] = useState(0);
  const [accumulatedLines, setAccumulatedLines] = useState<LinesCoverageFile[]>(
    [],
  );
  const [accumulatedInventory, setAccumulatedInventory] = useState<
    InventoryCoverageFile[]
  >([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [fileFilter, setFileFilter] = useState<FileCoverageFilter>('all');

  const scanId = selected.scanId ?? '';
  const projectId = selected.projectId ?? '';

  useEffect(() => {
    setCurrentOffset(0);
    setAccumulatedLines([]);
    setAccumulatedInventory([]);
    setTotalFiles(0);
    setDetailFilePath(null);
    setFileFilter('all');
  }, [scanId]);

  const {
    data,
    isPending,
    isPlaceholderData,
    isError,
    isFetching,
    error,
    refetch,
  } = useCoverageAggregateManual(scanId, projectId, currentOffset);

  useEffect(() => {
    if (!data?.data || isPlaceholderData) return;
    const { linesOfCodeMode, inventoryMode, pagination } = data.data;
    setTotalFiles(pagination.totalFiles);
    const pageOffset = pagination.offset ?? currentOffset;
    const newLines = linesOfCodeMode?.fileLevelCoverage ?? [];
    const newInventory = inventoryMode?.fileLevelCoverage ?? [];
    if (pageOffset === 0) {
      setAccumulatedLines(newLines);
      setAccumulatedInventory(newInventory);
      return;
    }
    setAccumulatedLines(prev => mergeCoverageFilesByPath(prev, newLines));
    setAccumulatedInventory(prev =>
      mergeCoverageFilesByPath(prev, newInventory),
    );
  }, [data, isPlaceholderData, currentOffset]);

  const hasMoreFiles = currentOffset + COVERAGE_FILES_PAGE_SIZE < totalFiles;

  const loadMore = useCallback(() => {
    setCurrentOffset(prev => prev + COVERAGE_FILES_PAGE_SIZE);
  }, []);

  useAutoLoadCoveragePages(
    hasMoreFiles,
    isFetching,
    isPlaceholderData,
    loadMore,
  );

  const visibleLines = filterLinesFiles(accumulatedLines, fileFilter);
  const visibleInventory = filterInventoryFiles(accumulatedInventory, fileFilter);
  const visibleFileCount =
    mode === 'lines' ? visibleLines.length : visibleInventory.length;

  const { mutate: triggerPoll } = useMutation({
    mutationFn: () => api.post('api/test-poll/trigger', {}),
  });

  const handleRefresh = () => {
    triggerPoll(undefined, { onSettled: () => void refetch() });
  };

  useEffect(() => {
    if (detailFilePath) {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [detailFilePath]);

  if (isLoading || (!ready && !notFound)) {
    return <CoverageDashboardSkeleton variant="manual" />;
  }
  if (notFound) return <ComponentNotFound />;

  if (!scanId) {
    return (
      <div className={coveragePageClass}>
        <CoverageStateMessage variant="empty">
          No component selected. Please select a component first.
        </CoverageStateMessage>
      </div>
    );
  }

  const aggregateData = data?.data;
  const detailFile = detailFilePath
    ? mode === 'lines'
      ? accumulatedLines.find(f => f.filePath === detailFilePath)
      : accumulatedInventory.find(f => f.filePath === detailFilePath)
    : undefined;
  const detailEntities: (EntityDetails | EntityDetailsInventory)[] =
    detailFile?.entityDetails ?? [];
  const viewCodeFile = viewCodeFilePath
    ? mode === 'lines'
      ? accumulatedLines.find(f => f.filePath === viewCodeFilePath)
      : accumulatedInventory.find(f => f.filePath === viewCodeFilePath)
    : undefined;
  const viewCodeEntities = viewCodeFile?.entityDetails ?? [];
  const summary =
    mode === 'lines'
      ? aggregateData?.linesOfCodeMode?.summary
      : aggregateData?.inventoryMode?.summary;
  const isFrontend = data?.data?.isFrontend;

  let API_COVERAGE = {};
  let UI_COVERAGE = {};
  if (isFrontend) {
    API_COVERAGE =
      mode === 'lines'
        ? data?.data?.otherRepos[0]?.derivedCoverage?.linesOfCodeMode
            ?.summary || {}
        : data?.data?.otherRepos[0]?.derivedCoverage?.inventoryMode?.summary ||
          {};
    UI_COVERAGE =
      mode === 'lines'
        ? data?.data?.coverage?.linesOfCodeMode?.summary || {}
        : data?.data?.coverage?.inventoryMode?.summary || {};
  } else {
    API_COVERAGE =
      mode === 'lines'
        ? data?.data?.coverage?.linesOfCodeMode?.summary || {}
        : data?.data?.coverage?.inventoryMode?.summary || {};
    UI_COVERAGE = {};
  }

  return (
    <div className={coveragePageClass}>
      <OnboardingPageHeader
        eyebrow="Coverage Analysis"
        title="Coverage Dashboard"
        subtitle="Comprehensive code coverage analysis — Manual Tests"
      />

      <OnboardingProjectHeader
        org={selected.orgName}
        project={selected.projectName || '—'}
        repoName={selected.repoName || '—'}
        branchName={
          (selected.branchName || 'main').replace(/^refs\/heads\//, '') || '—'
        }
      />

      <div className="mb-4 flex flex-wrap justify-end">
        <CoverageHistoryNavLink
          dbRepoId={selected.dbRepoId ?? ''}
          bucket="manual"
        />
      </div>

      <section className={coverageCardClass}>
        {aggregateData?.pollerState ? (
          <PollerBadges
            lastPolled={aggregateData.pollerState.lastTestPolledTime}
            nextPoll={aggregateData.pollerState.nextScheduledPoll}
          />
        ) : null}

        <CoverageModeToolbar
          mode={mode}
          onModeChange={nextMode => {
            setMode(nextMode);
            setDetailFilePath(null);
          }}
          isFetching={isFetching}
          onRefresh={handleRefresh}
        />

        {isPending && !aggregateData ? (
          <CoverageDashboardContentSkeleton variant="manual" />
        ) : null}

        {isError ? (
          <CoverageStateMessage variant="error">
            Failed to load coverage data: {(error as Error)?.message}
          </CoverageStateMessage>
        ) : null}

        {!isPending && !isError && !aggregateData ? (
          <CoverageStateMessage variant="empty">
            No coverage data available for this component. Run a scan to populate
            coverage.
          </CoverageStateMessage>
        ) : null}

        {aggregateData && summary ? (
          <>
            <CoverageModeExplanation mode={mode}>
              {mode === 'lines' ? (
                <>
                  Measures coverage based on executable lines of code — how many
                  lines were executed during testing (
                  {summary.totalCoverage?.covered?.toLocaleString() ?? '—'} /{' '}
                  {summary.totalCoverage?.total?.toLocaleString() ?? '—'} lines ={' '}
                  {summary.totalCoverage?.percentage?.toFixed(1) ?? '—'}%).
                </>
              ) : (
                <>
                  Measures coverage based on code elements (methods, branches,
                  loops, functions) — how many elements were tested regardless
                  of line count (
                  {summary.totalCoverage?.covered?.toLocaleString() ?? '—'} /{' '}
                  {summary.totalCoverage?.total?.toLocaleString() ?? '—'}{' '}
                  elements ={' '}
                  {summary.totalCoverage?.percentage?.toFixed(1) ?? '—'}%).
                </>
              )}
            </CoverageModeExplanation>

            <SummaryCards
              summary={summary}
              ui_coverage={UI_COVERAGE}
              api_coverage={API_COVERAGE}
              variant="manual"
            />

            {mode === 'inventory' &&
            aggregateData.inventoryMode?.coverageByCodeElement ? (
              <ElementBreakdown
                elements={aggregateData.inventoryMode.coverageByCodeElement}
              />
            ) : null}

            {(mode === 'lines' && accumulatedLines.length > 0) ||
            (mode === 'inventory' && accumulatedInventory.length > 0) ? (
              <FileCoverageFilterBar
                value={fileFilter}
                onChange={setFileFilter}
                shown={visibleFileCount}
                total={totalFiles}
                isLoading={hasMoreFiles && isFetching}
              />
            ) : null}

            {mode === 'lines' && visibleLines.length > 0 ? (
              <LinesTable
                files={visibleLines}
                onViewCode={setViewCodeFilePath}
                onViewDetails={setDetailFilePath}
              />
            ) : null}
            {mode === 'inventory' && visibleInventory.length > 0 ? (
              <InventoryTable
                files={visibleInventory}
                onViewCode={setViewCodeFilePath}
                onViewDetails={setDetailFilePath}
              />
            ) : null}

            {mode === 'lines' &&
            accumulatedLines.length > 0 &&
            visibleLines.length === 0 &&
            !hasMoreFiles ? (
              <CoverageStateMessage variant="empty">
                No files match the selected filter.
              </CoverageStateMessage>
            ) : null}
            {mode === 'inventory' &&
            accumulatedInventory.length > 0 &&
            visibleInventory.length === 0 &&
            !hasMoreFiles ? (
              <CoverageStateMessage variant="empty">
                No files match the selected filter.
              </CoverageStateMessage>
            ) : null}

            {detailFilePath ? (
              <div ref={detailRef}>
                <EntityDetailsPanel
                  filePath={detailFilePath}
                  entities={detailEntities}
                  showLocCoverage={mode === 'lines'}
                  onClose={() => setDetailFilePath(null)}
                  scanId={scanId}
                  onIgnoreSuccess={() => refetch()}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      <ViewCodeModal
        open={!!viewCodeFilePath}
        filePath={viewCodeFilePath ?? ''}
        selected={selected}
        coverageBucket="manual"
        entities={viewCodeEntities}
        showLocCoverage={mode === 'lines'}
        scanId={scanId}
        onIgnoreSuccess={() => refetch()}
        onClose={() => setViewCodeFilePath(null)}
      />
    </div>
  );
};

export default ComponentCoverageViewManual;
